import { Fragment, forwardRef, useRef } from "react";
import { bool, func, node, object, oneOfType, string } from "prop-types";
import styled from "styled-components/macro";
import { rem } from "polished";

import { displayMeter, formatNumber } from "../../../helpers";
import { colors, fonts } from "../../../styles/Styles.variables";
import { contacts, routes } from "../../../constants";

import Button from "../../../styles/components/Button";
import ButtonIcon from "../../../styles/components/ButtonIcon";
import ModalWatch from "../../modal-watch";
import SourceTypeLink from "../../source-type-link";
import WatchButton, { WatchButtonControl } from "../../watch-button";
import FilestackAdaptive from "../../filestack-adaptive";
import PhotosPlaceholder from "../../../styles/components/PhotosPlaceholder";

const ListingStacked = forwardRef((props, ref) => {
  const {
    analyticsLabel,
    authenticated,
    changes,
    className,
    handleCompare,
    handleWatch,
    isCompared,
    isWatched,
    isWatchPatch,
    linkProps,
    listing,
    openWatch,
    showStatus,
    targetDevice,
    trackEvent,
    trackModalView,
  } = props;

  const compareButton = useRef();

  const compareIcon = isCompared ? "starSolid" : "star";

  const price = listing.salePrice || listing.advertisedPrice;
  const showBuy =
    !authenticated &&
    listing.allowBuy &&
    listing.placement === "customer" &&
    price > 0;

  return (
    <div
      className={`${className}
        ${changes !== null ? "listing-has-changes" : ""}`}
    >
      {changes !== null && (
        <ListingChanges className="listing-changes" hasChanges={!!changes}>
          {changes}
        </ListingChanges>
      )}
      <div className="listing-row">
        <ListingPhoto className="listing-photo" {...linkProps}>
          <div className="listing-photo-container">
            {!!listing.photos &&
            !!Array.isArray(listing.photos) &&
            !!listing.photos.length &&
            !!listing.photos[0].liveCropped ? (
              <ListingPicture
                alt={`thumbnail of ${listing.equipmentId}`}
                fit="crop"
                handle={listing.photos[0].liveCropped}
                includeWarranty={listing.includeWarranty}
                newEquipment={listing.sourceType === "new"}
                pending={listing.status === "pending"}
                removed={listing.status === "remove"}
                sold={listing.status === "sold"}
                sizes={{
                  "1000px": { height: 303, width: 303 },
                  "481px": { height: 439, width: 768 },
                  "376px": { height: 275, width: 480 },
                  "0px": { height: 214, width: 375 },
                }}
                special={!!listing.salePrice}
                theme={targetDevice === "mobile" ? "dark" : "light"}
              />
            ) : (
              <ListingPicture as={PhotosPlaceholder} />
            )}
          </div>
        </ListingPhoto>
        <div className="listing-details">
          <ListingTitle className="listing-title">
            <ListingTitleLink {...linkProps}>
              {!!listing.year && <strong>{listing.year}</strong>}{" "}
              {!!listing.make && ` ${listing.make} `}
              {!!listing.make && !!listing.model && "– "}
              {!!listing.model && listing.model}
            </ListingTitleLink>
          </ListingTitle>
          <ListingLocation className="listing-location">
            {listing.locationCity}
            {!!listing.locationCity && !!listing.locationState && ", "}
            {listing.locationState} {listing.locationZip}
          </ListingLocation>
          <ListingMeta className="listing-meta">
            {(!listing.sourceType ||
              listing.sourceType === "used" ||
              (!showStatus && listing.sourceType === "new")) && (
              <li>
                <span className="label-block">Price</span>
                <ListingPrice listing={listing} showStatus={showStatus} />
              </li>
            )}
            {!showStatus &&
              listing.sourceType === "new" &&
              listing.financePrice &&
              listing.financePrice > 0 && (
                <>
                  <li>
                    <span className="label-block">60-month</span>
                    <ValueLarge>
                      {`$${formatNumber(listing.financePrice)}`}
                      <SmallLowerCase>/month</SmallLowerCase>
                    </ValueLarge>
                  </li>
                  <li className="listing-meta-break-xs listing-meta-break-md" />
                </>
              )}
            {(!listing.sourceType || listing.sourceType === "used") && (
              <li>
                <span className="label-block">{listing.meterType}</span>
                {displayMeter(listing) ? (
                  <ValueLarge>{formatNumber(listing.meterValue)}</ValueLarge>
                ) : (
                  <ValueLarge>–</ValueLarge>
                )}
              </li>
            )}
            {!showStatus && listing.sourceType === "new" && (
              <li>
                <span className="label-block">Quantity Available</span>
                {!!listing.numListings && listing.numListings > 0 ? (
                  <ValueLarge>{listing.numListings}</ValueLarge>
                ) : (
                  <ValueLarge>–</ValueLarge>
                )}
              </li>
            )}
            {(!listing.sourceType || listing.sourceType === "used") && (
              <li>
                <span className="label-block">Serial #</span>
                {!!listing.serial ? (
                  <ValueLarge>{listing.serial}</ValueLarge>
                ) : (
                  <ValueLarge>–</ValueLarge>
                )}
              </li>
            )}
          </ListingMeta>
          {listing.status !== "remove" && (
            <Fragment>
              {showBuy && (
                <p>
                  <StyledButton
                    analyticsAction="Buy Now"
                    analyticsLabel={analyticsLabel}
                    color="blue500"
                    size="small"
                    sourceType={listing.sourceType}
                    tag={SourceTypeLink}
                    to={`${routes.buy.path}/${listing.slug}`}
                  >
                    Buy Now
                  </StyledButton>
                </p>
              )}
              <p>
                <StyledButton
                  analyticsAction="View Details"
                  analyticsLabel={analyticsLabel}
                  color="yellow"
                  size="small"
                  sourceType={listing.sourceType}
                  tag={SourceTypeLink}
                  to={`/${listing.slug}`}
                >
                  Details
                </StyledButton>
              </p>
              <ListingActions>
                <ButtonIcon
                  icon={compareIcon}
                  iconColor={colors.blue500}
                  ref={compareButton}
                  type="button"
                  onClick={handleCompare}
                >
                  Compare
                </ButtonIcon>
                {/*
                    Watched listings API response doesn't include the
                    `sourceType` prop, so assuming it's allowed for any
                    listings that appear on the watched listings page
                  */}
                {(isWatchPatch || listing.sourceType === "used") && (
                  <>
                    {authenticated ? (
                      <WatchButton
                        listingId={listing.id}
                        isWatched={isWatched}
                        isWatchPatch={isWatchPatch}
                      />
                    ) : (
                      <WatchButtonControl
                        analyticsLabel={analyticsLabel}
                        onClick={handleWatch}
                        isWatchPatch={isWatchPatch}
                        ref={ref}
                        trackEvent={trackEvent}
                      />
                    )}
                  </>
                )}
              </ListingActions>
            </Fragment>
          )}
        </div>
      </div>
      {listing.status !== "remove" && (
        <ModalWatch
          analyticsLabel={analyticsLabel}
          close={handleWatch}
          listing={listing}
          listingId={!!listing && listing.id}
          open={openWatch}
          targetDevice={targetDevice}
          trackEvent={trackEvent}
          trackModalView={trackModalView}
        />
      )}
    </div>
  );
});

ListingStacked.propTypes = {
  analyticsLabel: string,
  authenticated: bool,
  changes: oneOfType([node, string]),
  className: string,
  handleCompare: func.isRequired,
  handleWatch: func.isRequired,
  isCompared: bool,
  isWatched: bool,
  isWatchPatch: bool,
  linkProps: object,
  listing: object,
  openWatch: bool,
  showStatus: bool,
  targetDevice: string,
  trackEvent: func,
  trackModalView: func,
};

function ListingPrice({ listing, showStatus }) {
  if (showStatus && listing.status === "pending") {
    return (
      <ValueLarge>
        <PhoneLink
          className="sold"
          href={`tel:1-${contacts.phone.main.hyphens}`}
        >
          SALE PENDING
        </PhoneLink>
      </ValueLarge>
    );
  }

  if (showStatus && listing.status === "sold") {
    return (
      <ValueLarge>
        <PhoneLink
          className="sold"
          href={`tel:1-${contacts.phone.main.hyphens}`}
        >
          SOLD
        </PhoneLink>
      </ValueLarge>
    );
  }

  if (!!listing.salePrice) {
    return (
      <Fragment>
        <ValueLarge special>{`$${formatNumber(listing.salePrice)}`}</ValueLarge>
        {!!listing.advertisedPrice && (
          <ValueListed>
            Listed <s>{`$${formatNumber(listing.advertisedPrice)}`}</s>
          </ValueListed>
        )}
      </Fragment>
    );
  }

  if (!!listing.advertisedPrice) {
    return <ValueLarge>${formatNumber(listing.advertisedPrice)}</ValueLarge>;
  }

  return (
    <ValueLarge>
      <PhoneLink href={`tel:1-${contacts.phone.main.hyphens}`}>
        Call for Pricing
      </PhoneLink>
    </ValueLarge>
  );
}

const PhoneLink = styled.a`
  color: ${colors.white} !important;
  text-decoration: none;

  &.sold {
    color: ${colors.red400} !important;
  }
`;

const SmallLowerCase = styled.small`
  text-transform: lowercase !important;
`;

const StyledButton = styled(Button)`
  margin-bottom: ${rem("4px")};
  width: 100%;
`;

const ListingPhoto = styled.div`
  display: block;

  &:focus {
    outline: ${rem("1px")} dotted;
    outline-offset: ${rem("2px")};
  }
`;

const ListingChanges = styled.div`
  background-color: ${colors.gray200};
  color: ${({ hasChanges }) => (hasChanges ? colors.red500 : colors.black)};
  padding: ${rem("8px")} ${rem("10px")};
  width: 100%;
`;

const ListingPicture = styled(FilestackAdaptive)`
  bottom: 0;
  display: block;
  left: 0;
  height: 100%;
  object-fit: cover;
  object-position: center center;
  overflow: hidden;
  position: absolute;
  right: 0%;
  top: 0;
  width: 100%;

  img {
    bottom: 0;
    display: block;
    height: 100%;
    left: 0;
    position: absolute;
    right: 0%;
    top: 0;
    width: 100%;
  }
`;

const ListingTitle = styled.h1`
  font-weight: ${fonts.weights.normal};
  line-height: 1;

  a {
    color: inherit;
    text-decoration: none;

    &:focus {
      outline: ${rem("1px")} dotted;
      outline-offset: ${rem("2px")};
    }
  }
`;

const ListingTitleLink = styled.span``;

const ListingLocation = styled.p`
  line-height: 1;
`;

const ValueLarge = styled.strong`
  color: ${({ special }) => (special ? colors.orange500 : "inherit")};
`;

const ValueListed = styled.span.attrs(({ className }) => ({
  className: `value-listed ${className}`,
}))`
  display: block;
  font-weight: ${fonts.weights.normal};
  line-height: 1;
`;

const ListingMeta = styled.ul`
  .label-block {
    display: block;
    text-transform: uppercase;
  }

  strong {
    display: block;
    word-break: break-all;
  }
`;

const ListingActions = styled.div`
  align-items: baseline;
  display: flex;
  flex-wrap: wrap;
  font-size: ${rem("14px")};
  gap: ${rem("16px")};
  margin-bottom: ${rem("6px")};
`;

export default ListingStacked;
